import { useEffect, useRef, useState } from "react";
import { Bell, X, MailWarning, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { brl, formatApiError } from "@/lib/format";

/**
 * EmailFalhoBell — 3º sininho no topbar (separado dos sinos boleto/nota).
 *
 * Filtra `tipo=email_falho`: notificações criadas pelo safety net do backend
 * quando um boleto atingiu 5 tentativas de envio de e-mail sem sucesso.
 * Cor VERMELHA (bg-rose-600 / text-rose-600) — distingue visualmente dos outros.
 *
 * Ao expandir o item, o usuário pode:
 *  - Clicar "Reenviar agora" → POST /api/boletos/{bid}/enviar-email (usa email
 *    do cliente atualizado no cadastro).
 *  - Clicar "Ver boleto" → navega pra /boletos.
 */
export default function EmailFalhoBell() {
  const [aberto, setAberto] = useState(false);
  const [itens, setItens] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [reenviando, setReenviando] = useState({}); // { boleto_id: true }
  const ref = useRef(null);
  const idsVistosRef = useRef(new Set());
  const primeiraCargaRef = useRef(true);

  const filtrar = (n) => n.tipo === "email_falho";

  const carregar = () =>
    api
      .get("/notificacoes")
      .then((r) => {
        const brutos = r.data.itens || [];
        const filtrados = brutos.filter(filtrar);
        const novos = filtrados.filter(
          (n) => !n.lida && !idsVistosRef.current.has(n.id)
        );
        if (novos.length > 0 && !primeiraCargaRef.current) {
          // Toast persistente pra chamar atenção — falha de email é crítico
          novos.forEach((n) => {
            toast.error(
              `E-mail não entregue — ${n.cliente_nome || "cliente"}`,
              {
                id: `email-falho-${n.id}`,
                description: `Boleto ${n.nosso_numero || ""} • ${brl(n.valor || 0)} • 5 tentativas`,
                duration: 8000,
                action: {
                  label: "Ver boletos",
                  onClick: () => {
                    window.location.href = "/boletos";
                  },
                },
              }
            );
          });
        }
        idsVistosRef.current = new Set(filtrados.map((n) => n.id));
        setItens(filtrados);
        setNaoLidas(filtrados.filter((n) => !n.lida).length);
        primeiraCargaRef.current = false;
      })
      .catch(() => {});

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 30000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    if (aberto) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [aberto]);

  const marcarLida = async (id) => {
    await api.put(`/notificacoes/${id}/lida`);
    carregar();
  };

  const marcarTodas = async () => {
    await Promise.all(
      itens.filter((n) => !n.lida).map((n) => api.put(`/notificacoes/${n.id}/lida`))
    );
    carregar();
  };

  const reenviar = async (n) => {
    const bid = n.boleto_id;
    if (!bid) return;
    setReenviando((s) => ({ ...s, [bid]: true }));
    try {
      const { data } = await api.post(`/boletos/${bid}/enviar-email`, {});
      if (data && data.email_id) {
        toast.success(
          `E-mail reenviado para ${n.cliente_nome || "cliente"}`,
          { description: (data.destinatarios || []).join(", ") || `Boleto ${n.nosso_numero || ""}` }
        );
        // Marca a notificação como lida (o problema foi resolvido manualmente)
        await api.put(`/notificacoes/${n.id}/lida`);
        carregar();
      } else {
        toast.warning("Reenvio disparado — verifique se chegou");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail ? formatApiError(err.response.data.detail) : (err?.message || "Erro");
      toast.error(`Falha ao reenviar: ${detail}`, { duration: 6000 });
    } finally {
      setReenviando((s) => {
        const c = { ...s };
        delete c[bid];
        return c;
      });
    }
  };

  const fmtTempo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const min = Math.floor((Date.now() - d) / 60000);
    if (min < 1) return "agora";
    if (min < 60) return `${min}m`;
    if (min < 1440) return `${Math.floor(min / 60)}h`;
    return `${Math.floor(min / 1440)}d`;
  };

  // Não renderiza quando não há falhas — evita ruído
  if (naoLidas === 0 && itens.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setAberto((v) => !v)}
        data-testid="notif-bell-email-falho"
        className={`relative w-9 h-9 rounded-full grid place-items-center hover:bg-slate-100 transition ${
          naoLidas > 0 ? "text-rose-600" : "text-slate-600"
        }`}
        title="E-mails com falha de envio"
      >
        <MailWarning className="w-5 h-5" />
        {naoLidas > 0 && (
          <span
            data-testid="notif-badge-email-falho"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
          >
            {naoLidas > 99 ? "99+" : naoLidas}
          </span>
        )}
      </button>

      {aberto && (
        <div
          className="absolute right-0 top-11 w-96 bg-white border border-slate-200 rounded-lg shadow-xl z-50"
          data-testid="notif-panel-email-falho"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-rose-50/60">
            <div>
              <div className="font-heading font-semibold text-slate-900 text-sm flex items-center gap-2">
                <MailWarning className="w-4 h-4 text-rose-600" />
                E-mails não entregues
              </div>
              <div className="text-xs text-slate-500">
                {naoLidas} não lida(s) — reenvie manualmente
              </div>
            </div>
            <div className="flex items-center gap-1">
              {naoLidas > 0 && (
                <button
                  onClick={marcarTodas}
                  data-testid="notif-bell-email-falho-marcar-todas"
                  className="text-xs text-rose-600 hover:text-rose-800 font-semibold"
                >
                  Marcar todas
                </button>
              )}
              <button
                onClick={() => setAberto(false)}
                className="w-7 h-7 grid place-items-center rounded hover:bg-slate-100"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
            {itens.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-400">
                <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
                Nenhuma falha de e-mail
              </div>
            )}
            {itens.map((n) => (
              <div
                key={n.id}
                data-testid={`notif-item-email-falho-${n.id}`}
                className={`p-3 hover:bg-slate-50 flex items-start gap-3 ${
                  !n.lida ? "bg-rose-50/40" : ""
                }`}
              >
                <div className="w-8 h-8 rounded-full grid place-items-center shrink-0 bg-rose-100">
                  <MailWarning className="w-4 h-4 text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold bg-rose-100 text-rose-700">
                      {n.provider || "banco"}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {fmtTempo(n.created_at)}
                    </span>
                    {!n.lida && (
                      <span className="ml-auto w-2 h-2 rounded-full bg-rose-500" />
                    )}
                  </div>
                  <div className="text-sm text-slate-800 mt-1 font-medium truncate">
                    {n.cliente_nome || "Cliente"}
                  </div>
                  <div className="text-xs text-slate-600 font-mono-fin">
                    {n.mensagem || "5 tentativas sem sucesso"}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {n.nosso_numero ? `Nº ${n.nosso_numero} • ` : ""}
                    {brl(n.valor || 0)}
                  </div>
                  {n.cliente_email && (
                    <div className="text-[10px] text-slate-400 truncate mt-0.5">
                      último e-mail tentado: {n.cliente_email}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => reenviar(n)}
                      disabled={!!reenviando[n.boleto_id]}
                      data-testid={`notif-reenviar-${n.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded"
                    >
                      <Send className="w-3 h-3" />
                      {reenviando[n.boleto_id] ? "Reenviando…" : "Reenviar agora"}
                    </button>
                    <button
                      onClick={() => {
                        window.location.href = "/boletos";
                      }}
                      className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Ver boleto
                    </button>
                  </div>
                </div>
                {!n.lida && (
                  <button
                    onClick={() => marcarLida(n.id)}
                    className="text-[10px] text-rose-600 hover:text-rose-800 font-semibold shrink-0"
                    data-testid={`notif-lida-${n.id}`}
                  >
                    Marcar
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
